import { CaretRight, Sparkle } from "@phosphor-icons/react";
import { Command } from "cmdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  actionsToCommandEntries,
  type CommandEntry,
  groupCommandEntries,
  pageTitle,
  resolveActionPath,
} from "~/actions/adapters/commandBar";
import { useActionContext } from "~/actions/context";
import { rootActions } from "~/actions/registry";
import { useThinkerDock } from "~/features/work/piles/pilesApi";
import { Kbd } from "~/foundation/components/Kbd";
import { useShellStore } from "~/shell/store";

function CommandRow({ entry, onSelect }: { entry: CommandEntry; onSelect: () => void }) {
  const IconComponent = entry.icon;
  return (
    <Command.Item
      value={`${entry.name} ${entry.keywords ?? ""} ${entry.id}`}
      onSelect={onSelect}
      className="flex h-10 cursor-pointer select-none items-center gap-2.5 rounded-sm px-3 text-body text-text data-[selected=true]:bg-menu-item-selected"
    >
      {IconComponent ? (
        <IconComponent size={16} aria-hidden className="shrink-0 text-text-tertiary" />
      ) : null}
      <span className="flex-1 truncate">{entry.name}</span>
      {entry.shortcut ? <Kbd keys={entry.shortcut} /> : null}
      {entry.hasChildren ? (
        <CaretRight size={12} aria-hidden className="text-text-tertiary rtl:rotate-180" />
      ) : null}
    </Command.Item>
  );
}

/**
 * Reports the empty state UP, from strictly BELOW the layer that registers items.
 *
 * kms learned this the hard way: subscribing the component that RENDERS the actions to kbar's
 * match state turned registration into a feedback loop that reset the open palette
 * continuously. cmdk has the same hazard shape, so the probe lives inside `Command.Empty` —
 * it can only ever run when cmdk has already decided nothing matched.
 */
function EmptyProbe({ onEmpty }: { onEmpty: (empty: boolean) => void }) {
  useEffect(() => {
    onEmpty(true);
    return () => onEmpty(false);
  }, [onEmpty]);
  return null;
}

/**
 * ⌘K. Content comes entirely from the action registry — nothing is declared twice, and an
 * action added anywhere in the app appears here for free.
 *
 * W7 adds the second half of the palette: the **Ask AI** mode tab and the fallthrough row that
 * appears when a query matches no action. Both carry whatever was typed into the Ask bar, which
 * is the whole point — a person who typed a sentence should not have to type it again.
 */
export function CommandBar() {
  const { t } = useTranslation();
  const open = useShellStore((state) => state.commandBarOpen);
  const setOpen = useShellStore((state) => state.setCommandBarOpen);
  const recentActionIds = useShellStore((state) => state.recentActionIds);

  const openAskBar = useCallback(
    (prompt = "") => {
      setOpen(false);
      useThinkerDock.getState().show(prompt);
    },
    [setOpen],
  );
  const [path, setPath] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [empty, setEmpty] = useState(false);
  const context = useActionContext({ source: "commandbar" });

  useEffect(() => {
    if (!open) {
      setPath([]);
      setQuery("");
    }
  }, [open]);

  const level = resolveActionPath(rootActions, path, context);
  const entries = actionsToCommandEntries(level, context);
  const title = pageTitle(rootActions, path, context);

  const groups = useMemo(() => {
    if (query.trim() === "" && path.length === 0 && recentActionIds.length > 0) {
      const recent = recentActionIds
        .map((id) => entries.find((entry) => entry.id === id))
        .filter((entry): entry is CommandEntry => entry !== undefined);
      const rest = entries.filter((entry) => !recent.includes(entry));
      return [
        ...(recent.length > 0 ? [{ section: t("Recent"), entries: recent }] : []),
        ...groupCommandEntries(rest),
      ];
    }
    return groupCommandEntries(entries);
  }, [entries, query, path.length, recentActionIds, t]);

  const select = useCallback(
    (entry: CommandEntry) => {
      if (entry.hasChildren) {
        setPath((current) => [...current, entry.id]);
        setQuery("");
        return;
      }
      entry.perform?.();
      setOpen(false);
    },
    [setOpen],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Backspace" && query === "" && path.length > 0) {
        event.preventDefault();
        setPath((current) => current.slice(0, -1));
        return;
      }
      // Nothing matched, so cmdk has no highlighted item and Enter would do nothing. Take it,
      // and stop it, so the palette's own selection logic cannot also fire on the way out.
      if (event.key === "Enter" && empty && query.trim() !== "") {
        event.preventDefault();
        event.stopPropagation();
        openAskBar(query.trim());
      }
    },
    [query, path.length, empty, openAskBar],
  );

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label={t("Command bar")}
      shouldFilter
      onKeyDown={onKeyDown}
      // cmdk portals its own overlay and content; their depths are pinned in app.css
      // against the [cmdk-overlay] / [cmdk-dialog] attributes it sets.
      overlayClassName="slate-overlay fixed inset-0 bg-backdrop"
      contentClassName="slate-pop fixed inset-x-0 top-[20vh] mx-auto w-[560px] max-w-[90vw] overflow-hidden rounded-md border border-divider bg-menu-bg shadow-menu"
    >
      <div className="flex items-center gap-1.5 border-b border-divider px-3 pt-2">
        <span className="rounded-sm bg-bg-secondary px-2 py-0.5 text-small font-medium text-text">
          {t("Commands")}
          <span className="ms-1.5 text-text-tertiary">⌘K</span>
        </span>
        <button
          type="button"
          onClick={() => openAskBar(query.trim())}
          className="rounded-sm px-2 py-0.5 text-small text-text-secondary hover:bg-hover-bg hover:text-text"
        >
          {t("Ask AI")}
          <span className="ms-1.5 text-text-tertiary">⌘⇧A</span>
        </button>
      </div>

      <div className="flex items-center gap-2 border-b border-divider px-3">
        {title ? (
          <span className="shrink-0 rounded-sm bg-bg-secondary px-2 py-0.5 text-small text-text-secondary">
            {title}
          </span>
        ) : null}
        <Command.Input
          value={query}
          onValueChange={setQuery}
          autoFocus
          placeholder={t("Type a command or search…")}
          className="h-12 flex-1 bg-transparent text-body text-text outline-none"
        />
      </div>

      <Command.List className="scrollbar-thin max-h-[320px] overflow-y-auto p-1">
        <Command.Empty className="flex flex-col gap-2 px-3 py-6 text-center text-body text-text-secondary">
          <EmptyProbe onEmpty={setEmpty} />
          {query.trim() === "" ? (
            t("No results found")
          ) : (
            <button
              type="button"
              onClick={() => openAskBar(query.trim())}
              className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-start text-body text-text hover:bg-menu-item-selected"
            >
              <Sparkle size={16} aria-hidden className="shrink-0 text-accent" />
              <span className="flex-1 truncate">
                {t("Ask AI: “{{query}}”", { query: query.trim() })}
              </span>
              <Kbd keys={["Enter"]} />
            </button>
          )}
        </Command.Empty>
        {groups.map((group) => (
          <Command.Group
            key={group.section}
            heading={group.section}
            className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-text-tertiary"
          >
            {group.entries.map((entry) => (
              <CommandRow key={entry.id} entry={entry} onSelect={() => select(entry)} />
            ))}
          </Command.Group>
        ))}
      </Command.List>
    </Command.Dialog>
  );
}
